import type { CopyBackend } from './persistentCopyStore';

const DB_NAME = 'oh-server-docs-copy';
const STORE_NAME = 'copies';

const settle = <T,>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/**
 * The persistent copy store's disk: one IndexedDB object store, opened on first use. No library: this app does not depend on idb-keyval.
 * Where IndexedDB is missing or will not open (some private-browsing modes), reads find nothing and deletes do nothing, and a write throws
 * so the store reports it once and the page carries on with its in-memory copy.
 */
export function createIdbBackend(factory: IDBFactory | null = typeof indexedDB === 'undefined' ? null : indexedDB): CopyBackend {
  let opened: Promise<IDBDatabase | null> | undefined;
  const open = (): Promise<IDBDatabase | null> => {
    if (!factory) return Promise.resolve(null);
    if (!opened) {
      opened = new Promise<IDBDatabase | null>((resolve) => {
        try {
          const req = factory.open(DB_NAME, 1);
          req.onupgradeneeded = () => { req.result.createObjectStore(STORE_NAME); };
          req.onsuccess = () => {
            const db = req.result;
            // A connection the browser closed (iOS Safari drops them when the app is backgrounded) or an upgrade elsewhere: forget it, so the next operation opens a fresh one.
            db.onclose = () => { opened = undefined; };
            db.onversionchange = () => { db.close(); opened = undefined; };
            resolve(db);
          };
          req.onerror = () => { opened = undefined; resolve(null); }; // a failed open is retried by the next operation, not remembered for the session
          req.onblocked = () => { opened = undefined; resolve(null); };
        } catch {
          opened = undefined;
          resolve(null);
        }
      });
    }
    return opened;
  };
  const begin = async (mode: IDBTransactionMode): Promise<IDBTransaction | null> => {
    const db = await open();
    if (!db) return null;
    try {
      return db.transaction(STORE_NAME, mode);
    } catch {
      // InvalidStateError: the connection was closed under us. Reopen once.
      opened = undefined;
      const fresh = await open();
      return fresh ? fresh.transaction(STORE_NAME, mode) : null;
    }
  };
  const store = async (mode: IDBTransactionMode): Promise<{ s: IDBObjectStore; done: Promise<void> } | null> => {
    const tx = await begin(mode);
    if (!tx) return null;
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
    // Only the write paths await `done`; a read that aborts is reported by its own request, so this must not become an unhandled rejection.
    done.catch(() => undefined);
    return { s: tx.objectStore(STORE_NAME), done };
  };
  return {
    async get(key) {
      const t = await store('readonly');
      return t ? settle(t.s.get(key)) : undefined;
    },
    async set(key, value) {
      const t = await store('readwrite');
      if (!t) throw new Error('IndexedDB unavailable');
      t.s.put(value, key);
      await t.done; // resolves only once the write is durable, so a full disk surfaces here
    },
    async del(key) {
      const t = await store('readwrite');
      if (!t) return;
      t.s.delete(key);
      await t.done;
    },
    async keys() {
      const t = await store('readonly');
      return t ? (await settle(t.s.getAllKeys())).filter((k): k is string => typeof k === 'string') : [];
    },
  };
}
